import {
  Box,
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
} from '@cambridgeassessment/cambridge-ui';
import { Typography } from '@material-ui/core';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Favicon from 'src/assets/img/favicon.png';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import { changeOrganisation } from 'src/services/gigya/gigya-service';
import type { CambridgeOrganisation, OrgAssociateCenter } from 'src/types';
import { getFlatOrgsLength } from 'src/utils/transformers';
import {
  DEFAULT_ERROR_MESSAGE,
  MIN_COUNT_TO_SHOW_SEARCHBAR,
} from '../../constants';
import ChangeOrganisationAccordianList from '../ChangeOrganisationAccordianList/ChangeOrganisationAccordianList';
import SearchField from '../SearchField/SearchField';
import type { Option } from '../Select/Select';
import { useStyles } from './styles';
export interface IChangeOrganisationDialog {
  open: boolean;
  onClose(selected?: boolean): void;
}

const getTitle = (organisation: CambridgeOrganisation): string => {
  const legacyNumber = organisation.sourceSystems?.find(
    (source) => source.systemId === 'LegacyCustomerNum'
  )?.orgId;
  return legacyNumber
    ? `${legacyNumber} - ${organisation.name}`
    : organisation.name;
};

const ChangeOrganisationDialog: React.FC<IChangeOrganisationDialog> = ({
  open,
  onClose,
}) => {
  const classes = useStyles();

  const {
    user: { organisationId: currentOrgId, organisations },
    update,
  } = useAuth();
  const { addNotification } = useNotifications();

  const [selectedOrgId, setSelectedOrgId] = useState<string>(currentOrgId);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [organisationList, setOrganisationList] = useState<Option[]>([]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const findOrg = useCallback(
    (orgId: string): CambridgeOrganisation | OrgAssociateCenter => {
      for (const element of organisations) {
        if (element.bpid === orgId) {
          return element;
        } else {
          for (let j = 0; j < element.childIds?.length; j++) {
            if (element?.childIds[j]?.bpid === orgId) {
              return element.childIds[j];
            }
          }
        }
      }
    },
    [organisations]
  );

  const getOrgNamebyId = (bpid: string): Option => {
    return (
      organisationList.find((obj) => obj.id === bpid) ||
      organisationList
        .reduce((acc, obj) => acc.concat(obj['childs'] || []), [])
        .find((childObj) => childObj.id === bpid)
    );
  };

  const handleSubmit = useCallback(() => {
    if (currentOrgId === selectedOrgId) {
      clearSearch();
      onClose(true);
      return;
    }

    changeOrganisation(findOrg(selectedOrgId), (data) => {
      if (data.status === 'OK') {
        if (organisationList) {
          addNotification({
            title: `Changed organisation to ${
              getOrgNamebyId(selectedOrgId).label
            }.`,
          });
          sessionStorage.removeItem('orgUsers');
          clearSearch();
          onClose(true);
          update();
        }
      } else {
        onClose(true);
        addNotification({
          variant: 'error',
          title: DEFAULT_ERROR_MESSAGE,
        });
      }
    });
  }, [
    currentOrgId,
    selectedOrgId,
    findOrg,
    clearSearch,
    onClose,
    organisationList,
    addNotification,
    getOrgNamebyId,
    update,
  ]);

  const closeModal = useCallback(() => {
    setSelectedOrgId(currentOrgId);
    clearSearch();
    onClose(false);
  }, [clearSearch, currentOrgId, onClose]);

  useEffect(() => {
    setOrganisationList(
      (searchQuery
        ? organisations.filter(
            (organisation) =>
              getTitle(organisation)
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              organisation?.childIds?.some((child) =>
                getTitle(child)
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              )
          )
        : organisations
      )
        .map((organisation) => ({
          id: organisation.bpid,
          label: getTitle(organisation),
          childOnly: organisation.childOnly,
          childs:
            organisation?.childIds
              ?.map((childOrganisation) => ({
                id: childOrganisation.bpid,
                label: getTitle(childOrganisation),
              }))
              .sort((child1, child2) =>
                child1.label.toUpperCase() < child2.label.toUpperCase() ? -1 : 1
              ) || [],
        }))
        .sort((obj1, obj2) =>
          obj1.label.toUpperCase() < obj2.label.toUpperCase() ? -1 : 1
        )
    );
  }, [searchQuery, organisations]);

  const updateSearchQuery = useCallback(
    (searchData) => setSearchQuery(searchData),
    []
  );

  const noResultsMessage = useMemo(
    () => (
      <Box textAlign="center" mt={2}>
        <Typography variant="body1">
          <Box display="flex" justifyContent="center">
            No results were found for&nbsp;
            <Typography variant="subtitle1">{searchQuery}</Typography>.
          </Box>
        </Typography>
        <Typography variant="body1">
          Check your spelling or try searching with another keyword.
        </Typography>
      </Box>
    ),
    [searchQuery]
  );

  // NOSONAR TODO: adjust css for full screen after CDS-683 resolved
  return (
    <Modal
      className={classes.modalPopup}
      size="large"
      open={open}
      onClose={closeModal}
    >
      <ModalTitle onClose={() => closeModal()}>
        <Box display="flex">
          <img
            src={Favicon}
            alt="Cambridge logo"
            width={25}
            height={25}
            className={classes.modalImage}
          />
          <Typography variant="h5" data-testid="modal-title">
            {'  '}
            Your organisations
          </Typography>
        </Box>
        <div className={classes.modalHeader}></div>
        <Typography
          className={classes.subtitle}
          variant="body1"
          data-testid="select-organisation-typo"
        >
          Select the organisation you want to switch to.
        </Typography>
        {getFlatOrgsLength(organisations) >= MIN_COUNT_TO_SHOW_SEARCHBAR && (
          <Box className={classes.searchField}>
            <SearchField
              searchQuery={searchQuery}
              placeholder="e.g. Cambridge Assessment"
              onSubmit={updateSearchQuery}
              onChange={updateSearchQuery}
            />
            {organisationList.length === 0 && noResultsMessage}
          </Box>
        )}
      </ModalTitle>
      <ModalContent className={classes.modalContent}>
        <ChangeOrganisationAccordianList
          options={organisationList}
          onSelect={setSelectedOrgId}
          selected={selectedOrgId}
          className={undefined}
        />
      </ModalContent>
      <ModalActions>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => closeModal()}
          data-testid="changeOrganisation-cancel"
          className={classes.CancelBtnColor}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          data-testid="changeOrganisation-continue"
          className={classes.ContinueBtnColor}
        >
          Continue
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default memo(ChangeOrganisationDialog);
